import { useCallback, useEffect, useMemo, useState } from "react";
import { AuthLayout } from "../layouts/AuthLayout";
import {
  getPublicBookingMeta,
  getPublicBookingSlots,
  publicBookAppointment,
} from "../../api/bookings";
import {
  buildMonthGrid,
  WEEKDAY_HEADERS,
  formatMonthTitleFr,
  formatYmdLocal,
  formatDayTitleFr,
} from "./bookings/bookingCalendarUtils";
import "./LoginPage.css";
import "./PublicBookingPage.css";

type SlotPick = { host_user_id: string; starts_at: string; ends_at: string };

type EnrichedSlot = SlotPick & { display_name: string };

function formatTimeFr(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("fr-CA", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

const HOST_PALETTE = ["#059669", "#0d9488", "#2563eb", "#7c3aed", "#c026d3", "#db2777"] as const;

function hostAccentColor(hostId: string): string {
  let h = 0;
  for (let i = 0; i < hostId.length; i++) h = (h + hostId.charCodeAt(i) * (i + 1)) % 997;
  return HOST_PALETTE[h % HOST_PALETTE.length];
}

function isBeforeTodayLocal(d: Date): boolean {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return x < t;
}

function formatSlotDurationLine(
  min: number | null | undefined,
  max: number | null | undefined
): string | null {
  if (min == null || Number.isNaN(min)) return null;
  const hi = max != null && !Number.isNaN(max) && max !== min ? max : null;
  if (hi != null) return `Durée des créneaux : ${min} à ${hi} min`;
  return `Durée du rendez-vous : ${min} min`;
}

function minutesFromSlot(pick: { starts_at: string; ends_at: string }): number | null {
  const a = new Date(pick.starts_at).getTime();
  const b = new Date(pick.ends_at).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return null;
  return Math.round((b - a) / 60000);
}

export function PublicBookingPage({ token }: { token: string }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<{
    title: string;
    organization_name: string;
    hosts: { id: string; display_name: string }[];
    slot_duration_minutes?: number | null;
    slot_duration_minutes_max?: number | null;
  } | null>(null);
  const [slotsPayload, setSlotsPayload] = useState<Awaited<ReturnType<typeof getPublicBookingSlots>> | null>(null);
  const [visibleHostIds, setVisibleHostIds] = useState<Set<string>>(new Set());
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth());
  const [selectedYmd, setSelectedYmd] = useState<string | null>(null);
  const [selected, setSelected] = useState<SlotPick | null>(null);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientNotes, setClientNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [confirmationEmailSent, setConfirmationEmailSent] = useState<boolean | null>(null);

  const load = useCallback(async () => {
    if (!token.trim()) {
      setError("Lien invalide : paramètre manquant.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const m = await getPublicBookingMeta(token);
      setMeta(m);
      const s = await getPublicBookingSlots(token, undefined, 45);
      setSlotsPayload(s);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Impossible de charger la page.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!meta?.hosts?.length) return;
    setVisibleHostIds(new Set(meta.hosts.map((h) => h.id)));
  }, [meta]);

  const hostsById = useMemo(() => {
    const m = new Map<string, string>();
    (meta?.hosts ?? []).forEach((h) => m.set(h.id, h.display_name));
    return m;
  }, [meta]);

  const slotsByYmd = useMemo(() => {
    const map = new Map<string, EnrichedSlot[]>();
    if (!slotsPayload?.hosts) return map;
    for (const h of slotsPayload.hosts) {
      if (!visibleHostIds.has(h.host_user_id)) continue;
      const name = h.display_name || hostsById.get(h.host_user_id) || "Intervenant";
      for (const sl of h.slots ?? []) {
        const ymd = formatYmdLocal(new Date(sl.starts_at));
        const row: EnrichedSlot = {
          host_user_id: h.host_user_id,
          display_name: name,
          starts_at: sl.starts_at,
          ends_at: sl.ends_at,
        };
        const arr = map.get(ymd) ?? [];
        arr.push(row);
        map.set(ymd, arr);
      }
    }
    for (const [, arr] of map) {
      arr.sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    }
    return map;
  }, [slotsPayload, visibleHostIds, hostsById]);

  const totalSlotCount = useMemo(() => {
    let n = 0;
    for (const [, arr] of slotsByYmd) n += arr.length;
    return n;
  }, [slotsByYmd]);

  const calendarCells = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const todayYmd = formatYmdLocal(new Date());

  const toggleHost = (id: string) => {
    setVisibleHostIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const selectAllHosts = () => {
    if (!meta?.hosts?.length) return;
    setVisibleHostIds(new Set(meta.hosts.map((h) => h.id)));
  };

  const goPrevMonth = () => {
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else setViewMonth((m) => m - 1);
  };

  const goNextMonth = () => {
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else setViewMonth((m) => m + 1);
  };

  const daySlots = selectedYmd ? slotsByYmd.get(selectedYmd) ?? [] : [];

  const handleBook = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    const name = clientName.trim();
    const email = clientEmail.trim().toLowerCase();
    if (!name || !email) {
      setError("Nom et courriel sont requis.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await publicBookAppointment(token, {
        host_user_id: selected.host_user_id,
        starts_at: selected.starts_at,
        ends_at: selected.ends_at,
        client_name: name,
        client_email: email,
        client_notes: clientNotes.trim() || null,
      });
      setConfirmationEmailSent(res.confirmation_email_sent ?? false);
      setDone(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Réservation impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token.trim()) {
    return (
      <AuthLayout className="auth-layout--compact">
        <div className="login-page">
          <div className="login-header">
            <div className="login-brand">XLFlow</div>
            <h1>Rendez-vous</h1>
            <p>Ce lien est incomplet. Demandez un nouveau lien à votre contact XLKey.</p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (loading) {
    return (
      <AuthLayout className="auth-layout--compact">
        <div className="public-booking px-1 py-4 text-sm text-muted">Chargement de vos disponibilités…</div>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout className="auth-layout--compact">
        <div className="public-booking-success">
          <div className="public-booking-brand">XLFlow</div>
          <h1>Merci !</h1>
          <p className="mb-4 text-[0.95rem] font-semibold text-text-primary">
            Votre rendez-vous est bien enregistré. Nous avons hâte de vous retrouver.
          </p>
          {confirmationEmailSent === true ? (
            <p>
              Vous et votre interlocuteur recevrez un courriel de confirmation avec une invitation calendrier (.ics).
            </p>
          ) : (
            <p>
              {confirmationEmailSent === false
                ? "Aucun courriel de confirmation n’a pu être envoyé depuis le serveur (configuration courriel). Conservez la date de votre côté."
                : "Conservez bien la date de votre rendez-vous."}
            </p>
          )}
        </div>
      </AuthLayout>
    );
  }

  const noHostSelected = visibleHostIds.size === 0;

  const durationLine = formatSlotDurationLine(meta?.slot_duration_minutes, meta?.slot_duration_minutes_max);

  return (
    <AuthLayout className="auth-layout--compact">
      <div className="public-booking">
        <header className="public-booking-hero">
          <div className="public-booking-brand">XLFlow</div>
          <p className="public-booking-welcome">Bienvenue — choisissez un moment qui vous convient</p>
          <h1>{meta?.title ?? "Prendre rendez-vous"}</h1>
          {durationLine ? (
            <p className="public-booking-duration" role="note">
              {durationLine}
            </p>
          ) : null}
          {meta?.organization_name ? (
            <p className="public-booking-sub">{meta.organization_name}</p>
          ) : (
            <p className="public-booking-sub">Réservez un créneau en quelques clics. Les jours en vert indiquent des disponibilités.</p>
          )}
        </header>

        {error && (
          <div
            className="rounded-lg border px-4 py-3 text-sm"
            style={{
              borderColor: "color-mix(in srgb, var(--color-danger, #dc2626) 40%, var(--border))",
              background: "color-mix(in srgb, var(--color-danger, #dc2626) 10%, var(--surface))",
              color: "var(--text-primary)",
            }}
            role="alert"
          >
            {error}
          </div>
        )}

        {!slotsPayload?.hosts?.length ? (
          <div className="public-booking-panel">
            <p className="m-0 text-sm text-muted">
              Aucun intervenant n’est associé à ce lien pour le moment. Revenez plus tard ou contactez-nous directement.
            </p>
          </div>
        ) : (
          <>
            <section className="public-booking-panel">
              <h2 className="public-booking-panel-title">Intervenants affichés</h2>
              <p className="mb-3 text-sm text-muted">
                Par défaut, tous les créneaux sont visibles. Désélectionnez un nom pour le masquer sur le calendrier.
              </p>
              <div className="flex flex-wrap gap-2">
                <button type="button" className="public-booking-chip" onClick={selectAllHosts}>
                  Tout afficher
                </button>
                {meta?.hosts.map((h) => {
                  const on = visibleHostIds.has(h.id);
                  return (
                    <button
                      key={h.id}
                      type="button"
                      className={`public-booking-chip ${on ? "" : "public-booking-chip--off"}`}
                      onClick={() => toggleHost(h.id)}
                    >
                      <span
                        className="public-booking-chip-dot"
                        style={{ background: hostAccentColor(h.id) }}
                        aria-hidden
                      />
                      {h.display_name}
                    </button>
                  );
                })}
              </div>
              {noHostSelected && (
                <p className="mt-3 text-sm font-semibold text-amber-700 dark:text-amber-400">
                  Sélectionnez au moins un intervenant pour voir des créneaux.
                </p>
              )}
            </section>

            <div className="public-booking-layout">
              <section className="public-booking-panel">
                <h2 className="public-booking-panel-title">Calendrier</h2>
                {slotsPayload.from_date && slotsPayload.to_date && (
                  <p className="mb-3 text-xs text-muted">
                    Disponibilités chargées du{" "}
                    <strong className="text-text-primary">
                      {new Intl.DateTimeFormat("fr-CA", { dateStyle: "medium" }).format(new Date(slotsPayload.from_date + "T12:00:00"))}
                    </strong>{" "}
                    au{" "}
                    <strong className="text-text-primary">
                      {new Intl.DateTimeFormat("fr-CA", { dateStyle: "medium" }).format(new Date(slotsPayload.to_date + "T12:00:00"))}
                    </strong>
                    {totalSlotCount > 0 ? (
                      <span> — {totalSlotCount} créneau{totalSlotCount > 1 ? "x" : ""} au total</span>
                    ) : (
                      <span> — aucun créneau libre dans cette fenêtre (délai minimum, conflits ou règles côté équipe).</span>
                    )}
                  </p>
                )}

                <div className="public-booking-cal-nav">
                  <button type="button" onClick={goPrevMonth} aria-label="Mois précédent">
                    ‹
                  </button>
                  <span className="public-booking-month-title">{formatMonthTitleFr(viewYear, viewMonth)}</span>
                  <button type="button" onClick={goNextMonth} aria-label="Mois suivant">
                    ›
                  </button>
                </div>

                <div className="public-booking-weekdays">
                  {WEEKDAY_HEADERS.map((w) => (
                    <span key={w}>{w}</span>
                  ))}
                </div>
                <div className="public-booking-grid">
                  {calendarCells.map((cell, idx) => {
                    if (!cell) {
                      return <div key={`e-${idx}`} className="public-booking-cell public-booking-cell--empty" />;
                    }
                    const ymd = formatYmdLocal(cell);
                    const past = isBeforeTodayLocal(cell);
                    const count = slotsByYmd.get(ymd)?.length ?? 0;
                    const has = count > 0 && !noHostSelected;
                    const isToday = ymd === todayYmd;
                    const isSel = selectedYmd === ymd;
                    return (
                      <button
                        key={ymd}
                        type="button"
                        className={[
                          "public-booking-cell",
                          past ? "public-booking-cell--muted" : "",
                          has ? "public-booking-cell--has-slots" : "",
                          isToday ? "public-booking-cell--today" : "",
                          isSel ? "public-booking-cell--selected" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        disabled={!has || past}
                        onClick={() => {
                          if (!has || past) return;
                          setSelectedYmd(ymd);
                          setSelected(null);
                        }}
                      >
                        <span>{cell.getDate()}</span>
                        {has ? <span className="public-booking-cell-dot" aria-hidden /> : null}
                      </button>
                    );
                  })}
                </div>
              </section>

              <div className="flex flex-col gap-4">
                <section className="public-booking-panel">
                  <h2 className="public-booking-panel-title">Créneau du jour</h2>
                  {!selectedYmd ? (
                    <p className="m-0 text-sm text-muted">Cliquez sur un jour vert pour afficher les heures disponibles.</p>
                  ) : daySlots.length === 0 ? (
                    <p className="m-0 text-sm text-muted">Aucun créneau pour ce jour avec le filtre actuel.</p>
                  ) : (
                    <>
                      <p className="mb-3 text-sm font-medium text-text-primary">{formatDayTitleFr(new Date(selectedYmd + "T12:00:00"))}</p>
                      <div className="flex flex-col gap-2">
                        {daySlots.map((sl) => {
                          const active =
                            selected?.host_user_id === sl.host_user_id && selected.starts_at === sl.starts_at;
                          return (
                            <button
                              key={`${sl.host_user_id}-${sl.starts_at}`}
                              type="button"
                              className="flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition"
                              style={{
                                borderColor: active ? hostAccentColor(sl.host_user_id) : "var(--border)",
                                background: active
                                  ? `color-mix(in srgb, ${hostAccentColor(sl.host_user_id)} 12%, var(--surface))`
                                  : "var(--surface)",
                              }}
                              onClick={() =>
                                setSelected({
                                  host_user_id: sl.host_user_id,
                                  starts_at: sl.starts_at,
                                  ends_at: sl.ends_at,
                                })
                              }
                            >
                              <span className="font-semibold">{formatTimeFr(sl.starts_at)}</span>
                              <span className="public-booking-slot-pill text-xs text-muted">
                                <span
                                  className="inline-block h-2 w-2 rounded-full"
                                  style={{ background: hostAccentColor(sl.host_user_id) }}
                                />
                                {sl.display_name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {selected ? (
                        (() => {
                          const dm = minutesFromSlot(selected);
                          return dm != null ? (
                            <p className="mt-3 mb-0 text-sm font-medium text-text-primary">
                              Durée de ce créneau : {dm} min
                            </p>
                          ) : null;
                        })()
                      ) : null}
                    </>
                  )}
                </section>

                <section className="public-booking-form-card">
                  <h2>Vos informations</h2>
                  <p className="-mt-2 mb-4 text-sm text-muted">Complétez les champs ci-dessous pour confirmer votre rendez-vous.</p>
                  <form onSubmit={handleBook} className="space-y-0">
                    <div className="pb-field">
                      <label className="pb-label" htmlFor="pb-name">
                        Nom complet <span className="pb-label-hint">(requis)</span>
                      </label>
                      <input
                        id="pb-name"
                        className="pb-input"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        required
                        autoComplete="name"
                        disabled={submitting}
                        placeholder="Prénom Nom"
                      />
                    </div>
                    <div className="pb-field">
                      <label className="pb-label" htmlFor="pb-email">
                        Courriel <span className="pb-label-hint">(requis)</span>
                      </label>
                      <input
                        id="pb-email"
                        type="email"
                        className="pb-input"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                        required
                        autoComplete="email"
                        disabled={submitting}
                        placeholder="vous@exemple.com"
                      />
                    </div>
                    <div className="pb-field">
                      <label className="pb-label" htmlFor="pb-notes">
                        Notes <span className="pb-label-hint">(optionnel)</span>
                      </label>
                      <textarea
                        id="pb-notes"
                        className="pb-textarea"
                        value={clientNotes}
                        onChange={(e) => setClientNotes(e.target.value)}
                        disabled={submitting}
                        placeholder="Précisions utiles pour l’intervenant…"
                      />
                    </div>
                    <button type="submit" className="pb-submit" disabled={submitting || !selected}>
                      {submitting ? "Envoi en cours…" : "Confirmer mon rendez-vous"}
                    </button>
                    {!selected && (
                      <p className="mt-3 text-center text-xs text-muted">
                        Sélectionnez d’abord un jour vert puis un horaire pour activer la confirmation.
                      </p>
                    )}
                  </form>
                </section>
              </div>
            </div>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
